import { useInfiniteQuery } from 'react-query';

import type { GraphicsSearchResults } from '@components/pages/Graphics/SearchPage/interfaces';
import { GRAPHICS_SEARCH_RESULTS_KEY } from '@utils/backend-api/graphics';
import { getGraphicsSearchResults } from '@utils/backend-api/graphics';

type UseGraphicsSearchResultsParams = {
  filter?: string;
  limit: number;
  query: string;
  initialSearchResults?: GraphicsSearchResults[];
  initialCurrentPage?: number;
  initialPageCount?: number;
};

export default function useGraphicsSearchResults({
  filter,
  limit,
  query,
  initialSearchResults,
  initialCurrentPage = 1,
  initialPageCount = 1,
}: UseGraphicsSearchResultsParams) {
  const hasInitialData =
    initialSearchResults !== undefined &&
    initialCurrentPage !== undefined &&
    initialPageCount !== undefined;
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery(
    [GRAPHICS_SEARCH_RESULTS_KEY, filter, limit, query],
    async ({ pageParam = initialCurrentPage + 1 }) =>
      await getGraphicsSearchResults({
        filter: filter,
        limit: limit,
        page: pageParam,
        query: query,
      }),
    {
      initialData: hasInitialData
        ? {
            pages: [
              {
                currentPage: initialCurrentPage,
                pageCount: initialPageCount,
                searchResults: initialSearchResults,
              },
            ],
            pageParams: [initialCurrentPage],
          }
        : undefined,
      getNextPageParam: (lastPage) =>
        lastPage.currentPage < lastPage.pageCount ? lastPage.currentPage + 1 : undefined,
    }
  );

  const graphicsSearchResults = data?.pages.flatMap((page) => page.searchResults) || [];

  return {
    graphicsSearchResults,
    isLoadingGraphicsSearchResults: isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
}
